import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiPackage, FiCalendar } from 'react-icons/fi';

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const daysUntil = (d) => {
  if (!d) return null;
  const diff = Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
};

const ShipmentRow = ({ item }) => {
  const days   = daysUntil(item.csd_committed);
  const urgent = days !== null && days <= 3;
  return (
    <a
      href={`/crm/projects/${item.deal_id}`}
      target="_blank"
      rel="noreferrer"
      className="tw-flex tw-items-start tw-gap-3 tw-px-3 tw-py-2.5 tw-rounded hover:tw-bg-gray-50 tw-transition-colors tw-no-underline tw-block"
    >
      <div className="tw-flex-shrink-0 tw-mt-0.5">
        <FiCalendar size={13} className={urgent ? 'tw-text-red-500' : 'tw-text-gray-400'} />
      </div>
      <div className="tw-flex-1 tw-min-w-0">
        <p className="tw-text-xs tw-font-semibold tw-text-gray-800 tw-truncate">{item.project_number || '—'}</p>
        <p className="tw-text-xs tw-text-gray-500 tw-truncate">{item.company_name || '—'}</p>
        {item.name && (
          <p className="tw-text-xs tw-text-gray-600 tw-truncate">{item.name}</p>
        )}
      </div>
      <div className="tw-flex-shrink-0 tw-text-right">
        <p className={`tw-text-xs tw-font-bold ${urgent ? 'tw-text-red-600' : 'tw-text-gray-700'}`}>{fmtDate(item.csd_committed)}</p>
        {days !== null && (
          <p className={`tw-text-xs ${days <= 0 ? 'tw-text-red-500' : urgent ? 'tw-text-orange-500' : 'tw-text-gray-400'}`}>
            {days <= 0 ? 'Overdue' : `${days}d`}
          </p>
        )}
        {item.shipment_count && (
          <span className="tw-inline-block tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-text-xs tw-font-medium tw-mt-0.5 tw-bg-blue-100 tw-text-blue-700">
            {item.shipment_count} shipment{item.shipment_count > 1 ? 's' : ''}
          </span>
        )}
      </div>
    </a>
  );
};
ShipmentRow.propTypes = {
  item: PropTypes.shape({
    csd_committed: PropTypes.string,
    deal_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    project_number: PropTypes.string,
    company_name: PropTypes.string,
    name: PropTypes.string,
    shipment_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
};

const UpcomingShipmentsChart = ({ data, loading, error }) => {
  const [tab, setTab] = useState('week');

  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading shipment data</p>
    </Card>
  );

  const d         = data || {};
  const weekData  = d.this_week  || { count: 0, items: [] };
  const monthData = d.this_month || { count: 0, items: [] };
  const items     = tab === 'week' ? weekData.items : monthData.items;

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-teal-50 tw-via-white tw-to-cyan-50 tw-shadow-sm">
      <div className="tw-p-5 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-justify-between">
        <div>
          <h3 className="tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
            <FiPackage className="tw-text-teal-600" size={18} />
            Upcoming Shipments
          </h3>
          <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">CSD committed delivery schedule</p>
        </div>
        <div className="tw-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-rounded tw-p-1">
          <button
            type="button"
            onClick={() => setTab('week')}
            className={`tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-sm tw-transition-colors tw-cursor-pointer tw-border-0 ${
              tab === 'week' ? 'tw-bg-white tw-text-teal-700 tw-shadow-sm' : 'tw-bg-transparent tw-text-gray-500 hover:tw-text-gray-700'
            }`}
          >
            This Week
            {weekData.count > 0 && (
              <span className="tw-ml-1 tw-inline-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-teal-600 tw-text-white tw-text-xs">
                {weekData.count}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab('month')}
            className={`tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-sm tw-transition-colors tw-cursor-pointer tw-border-0 ${
              tab === 'month' ? 'tw-bg-white tw-text-teal-700 tw-shadow-sm' : 'tw-bg-transparent tw-text-gray-500 hover:tw-text-gray-700'
            }`}
          >
            Next 30 Days
            {monthData.count > 0 && (
              <span className="tw-ml-1 tw-inline-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-teal-600 tw-text-white tw-text-xs">
                {monthData.count}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="tw-p-3 tw-max-h-72 tw-overflow-y-auto">
        {items.length === 0 ? (
          <div className="tw-text-center tw-py-10">
            <FiPackage size={28} className="tw-text-gray-200 tw-mx-auto tw-mb-2" />
            <p className="tw-text-sm tw-text-gray-400">
              No shipments due {tab === 'week' ? 'this week' : 'in the next 30 days'}
            </p>
          </div>
        ) : (
          <div className="tw-space-y-0.5">
            {items.map((item) => <ShipmentRow key={item.id} item={item} />)}
          </div>
        )}
      </div>

      <div className="tw-px-5 tw-py-3 tw-border-t tw-border-gray-100">
        <p className="tw-text-xs tw-text-gray-400">
          Total in next 30 days: <span className="tw-font-semibold tw-text-gray-600">{(weekData.count || 0) + (monthData.count || 0)} shipments</span>
        </p>
      </div>
    </Card>
  );
};

UpcomingShipmentsChart.propTypes = {
  data:    PropTypes.object,
  loading: PropTypes.bool,
  error:   PropTypes.any
};
UpcomingShipmentsChart.defaultProps = { data: null, loading: false, error: null };

export default UpcomingShipmentsChart;
